import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import img1 from '../assets/images/projects/eugene-gasanze/1.jpg';
import img2 from '../assets/images/projects/eugene-kanombe/1.jpg';
import img3 from '../assets/images/projects/birembo/1.jpg';
import img4 from '../assets/images/projects/muya-apartment/1.jpg';
import img5 from '../assets/images/projects/kanombe/Kanombe1.jpg';

const testimonials = [
  {
    projectImage: img1,
    projectName: 'Gasanze Residential Homes',
    projectLocation: 'Gasanze, Kigali',
    quote: 'The EDGES team transformed our vision into a stunning, sustainable home. Their attention to detail and professionalism is truly unmatched.',
    name: 'Alice Niyonsaba',
    role: 'Managing Director',
    company: 'Prime Estates Ltd',
    avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
  },
  {
    projectImage: img2,
    projectName: 'Kanombe Eco Apartments',
    projectLocation: 'Kanombe, Kigali',
    quote: 'From concept to completion, Edges delivered beyond expectations. Their structural expertise gave us full confidence at every stage of the build.',
    name: 'Samuel Karekezi',
    role: 'Project Manager',
    company: 'CityBuild Contractors',
    avatar: 'https://randomuser.me/api/portraits/men/60.jpg',
  },
  {
    projectImage: img3,
    projectName: 'Birembo Commercial Block',
    projectLocation: 'Birembo, Kigali',
    quote: 'What impressed me most was their ability to blend modern design with functionality. Our commercial complex now stands out beautifully in the city.',
    name: 'Christine Uwase',
    role: 'Real Estate Developer',
    company: 'Vista Holdings',
    avatar: 'https://randomuser.me/api/portraits/women/41.jpg',
  },
  {
    projectImage: img4,
    projectName: 'Muya Luxury Apartments',
    projectLocation: 'Kacyiru, Kigali',
    quote: 'Edges Design made the entire process smooth and stress-free. Their team was responsive, creative, and deeply committed to quality from day one.',
    name: 'Emmanuel Habiyambere',
    role: 'Founder',
    company: 'Habiyambere Construction Co.',
    avatar: 'https://randomuser.me/api/portraits/men/35.jpg',
  },
  {
    projectImage: img5,
    projectName: 'Kanombe Residential Estate',
    projectLocation: 'Kanombe, Kigali',
    quote: 'An incredible team that truly listens. They delivered a well-planned estate that our residents are proud to call home.',
    name: 'Grace Mukamana',
    role: 'Property Developer',
    company: 'Kigali Living Ltd',
    avatar: 'https://randomuser.me/api/portraits/women/22.jpg',
  },
];

const INTERVAL = 9000;
const n = testimonials.length;

// Visual config for each depth (0 = front, 1 = mid, 2 = back)
const DEPTH = [
  { zIndex: 30, scale: 1,    x: 0,  y: 0,  rotate: -1, opacity: 1    },
  { zIndex: 20, scale: 0.92, x: 22, y: 18, rotate: 3,  opacity: 0.72 },
  { zIndex: 10, scale: 0.84, x: 42, y: 34, rotate: 7,  opacity: 0.42 },
];

const EASE = [0.25, 0.46, 0.45, 0.94];
const DUR = 0.55;

export default function Testimonial() {
  const [direction, setDirection] = useState(1);
  const dirRef = useRef(1);

  // cards: [{key, imgIdx, isNew}] — always 3 items, cards[0] is the front
  const [cards, setCards] = useState(() =>
    [0, 1, 2].map((i) => ({ key: String(i), imgIdx: i, isNew: false }))
  );
  const nextKey = useRef(n);

  // Single source of truth: front card's imgIdx drives everything
  const activeIdx = cards[0].imgIdx;

  const go = useCallback((dir) => {
    dirRef.current = dir;
    setDirection(dir);
    setCards((prev) => {
      const k = String(nextKey.current++);
      if (dir > 0) {
        const newImgIdx = (prev[prev.length - 1].imgIdx + 1) % n;
        return [...prev.slice(1), { key: k, imgIdx: newImgIdx, isNew: true }];
      } else {
        const newImgIdx = (prev[0].imgIdx - 1 + n) % n;
        return [{ key: k, imgIdx: newImgIdx, isNew: true }, ...prev.slice(0, -1)];
      }
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => go(1), INTERVAL);
    return () => clearTimeout(timer);
  }, [activeIdx, go]);

  const textVariants = {
    enter: (dir) => ({ x: dir > 0 ? 36 : -36, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit:  (dir) => ({ x: dir > 0 ? -36 : 36, opacity: 0 }),
  };

  const t = testimonials[activeIdx];

  return (
    <section id="testimonials" className="relative overflow-hidden bg-teal-900">

      {/* Background glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-teal-500/15 rounded-full blur-[130px] -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal-600/15 rounded-full blur-[110px] translate-y-1/2 -translate-x-1/4" />
      </div>

      {/* Dot-grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      {/* ── Section header ── */}
      <div className="relative text-center pt-16 pb-10 px-6">
        <div className="flex items-center justify-center gap-3 mb-4">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-teal-400 shadow-[0_0_8px_3px_rgba(94,234,212,0.7)]" />
            <span className="w-1.5 h-1.5 rounded-full bg-teal-300 shadow-[0_0_6px_2px_rgba(94,234,212,0.5)]" />
            <span className="w-1 h-1 rounded-full bg-teal-200 shadow-[0_0_4px_1px_rgba(94,234,212,0.3)]" />
          </span>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-400">Client Stories</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-white">
          What Our <span className="text-teal-300">Clients Say</span>
        </h2>
        <p className="mt-3 text-teal-200/60 text-sm max-w-md mx-auto">
          Real words from clients whose spaces we brought to life.
        </p>
      </div>

      {/* ── Split layout ── */}
      <div className="relative flex flex-col lg:flex-row">

        {/* ─── LEFT: Stacked project images ─── */}
        <div className="relative lg:w-1/2 flex items-center justify-center px-8 pb-8 pt-2 lg:py-10 lg:px-12 min-h-[300px] lg:min-h-[420px]">
          {/*
            Container is padded on the right/bottom so the back-of-stack cards
            (which shift +42px x, +34px y) are visible rather than clipped.
          */}
          <div className="relative w-full max-w-[420px]" style={{ paddingBottom: 34, paddingRight: 42 }}>
            {/* Actual card frame — aspect ratio 4:3 */}
            <div className="relative w-full" style={{ paddingBottom: '75%' }}>
              <AnimatePresence initial={false}>
                {cards.map(({ key, imgIdx, isNew }, depth) => (
                  <motion.div
                    key={key}
                    className="absolute inset-0 rounded-2xl overflow-hidden shadow-2xl"
                    initial={isNew
                      ? (dirRef.current > 0
                          ? { x: 52, y: 44, scale: 0.76, opacity: 0, rotate: 9,  zIndex: 5 }
                          : { x: -52, y: 44, scale: 0.76, opacity: 0, rotate: -9, zIndex: 5 })
                      : false
                    }
                    animate={DEPTH[depth]}
                    exit={dirRef.current > 0
                      ? { x: -110, y: -80, scale: 0.68, opacity: 0, rotate: -18, zIndex: 40 }
                      : { x:  110, y: -80, scale: 0.68, opacity: 0, rotate:  18, zIndex: 40 }
                    }
                    transition={{ duration: DUR, ease: EASE }}
                  >
                    <img
                      src={testimonials[imgIdx].projectImage}
                      alt={testimonials[imgIdx].projectName}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                    />

                    {/* Tint for non-front cards */}
                    {depth > 0 && (
                      <div className="absolute inset-0 bg-teal-900/30" />
                    )}

                    {/* Project badge on the front card only */}
                    {depth === 0 && (
                      <div className="absolute bottom-3 left-3 right-3">
                        <div className="inline-flex items-center gap-2 bg-black/45 backdrop-blur-md border border-white/15 text-white px-3 py-2 rounded-xl">
                          <span className="w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs font-semibold leading-tight truncate">{testimonials[imgIdx].projectName}</p>
                            <p className="text-[10px] text-white/55 mt-0.5">{testimonials[imgIdx].projectLocation}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* ─── RIGHT: Testimonial text ─── */}
        <div className="relative lg:w-1/2 flex flex-col justify-center px-8 md:px-12 py-8 lg:py-10">

          {/* Decorative large quote mark */}
          <span className="absolute top-4 right-8 text-[88px] leading-none font-serif text-teal-700/30 select-none pointer-events-none">"</span>

          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={`text-${activeIdx}`}
              custom={direction}
              variants={textVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: DUR, ease: EASE }}
            >
              {/* Stars */}
              <div className="flex gap-1 mb-5">
                {[...Array(5)].map((_, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05, duration: 0.2 }}
                    className="text-yellow-400 text-base"
                  >★</motion.span>
                ))}
              </div>

              {/* Quote */}
              <p className="text-white/90 text-base md:text-lg leading-relaxed font-light italic mb-7 max-w-lg">
                "{t.quote}"
              </p>

              {/* Divider */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.18, duration: 0.4 }}
                className="w-8 h-px bg-teal-400/50 mb-6 origin-left"
              />

              {/* Author */}
              <div className="flex items-center gap-3">
                <div className="relative shrink-0">
                  <div className="absolute inset-0 rounded-full bg-teal-400/20 blur-md scale-125" />
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="relative w-10 h-10 rounded-full object-cover border-2 border-teal-400/50"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">{t.name}</p>
                  <p className="text-teal-300/70 text-xs mt-0.5">{t.role} · {t.company}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Dot indicators only */}
          <div className="flex items-center gap-2 mt-8">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => { const dir = i > activeIdx ? 1 : -1; go(dir); }}
                aria-label={`Testimonial ${i + 1}`}
                className={`rounded-full transition-all duration-300 ${
                  i === activeIdx ? 'w-6 h-2 bg-teal-400' : 'w-2 h-2 bg-white/25 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="pb-12" />
    </section>
  );
}
